import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Loader2, PenLine } from 'lucide-react';
import { fileToUploadB64, resolveUrl } from './driverUtils';
import { PhotoRow } from './JobInfo';
import { SignaturePad } from './SignaturePad';

const UploadBtn = ({ kind, label, job, authFetch, onUpdated }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8);
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      try {
        const b64 = await fileToUploadB64(f);
        await authFetch('post', `/jobs/${job.id}/proof`, { kind, photos: [b64] });
        ok += 1;
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Couldn't upload ${f.name}`);
      }
    }
    if (ok) toast.success(`${ok} ${kind} photo${ok > 1 ? 's' : ''} saved.`);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
    onUpdated?.();
  };
  return (
    <>
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="h-11 px-3 rounded-md border border-slate-200 bg-white text-sm font-bold text-[color:var(--mv-navy)] inline-flex items-center gap-1.5 hover:border-[color:var(--mv-orange)]" data-testid={`proof-${kind}-btn-${job.ref}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4 text-[color:var(--mv-orange)]" />} {label}
      </button>
      <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={onFiles} data-testid={`proof-${kind}-input-${job.ref}`} />
    </>
  );
};

export const ProofPanel = ({ job, authFetch, onUpdated }) => {
  const [signing, setSigning] = useState(false);
  const [savingSig, setSavingSig] = useState(false);
  const before = (job.proof_photos || []).filter(p => p.kind === 'before');
  const after = (job.proof_photos || []).filter(p => p.kind === 'after');

  const saveSignature = async (dataUrl) => {
    setSavingSig(true);
    try {
      await authFetch('post', `/jobs/${job.id}/proof`, { kind: 'signature', photos: [dataUrl] });
      toast.success('Signature saved.');
      setSigning(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save signature.");
    } finally { setSavingSig(false); }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid={`proof-panel-${job.ref}`}>
      <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Proof of job</div>
      <div className="flex flex-wrap gap-2">
        <UploadBtn kind="before" label={`Before (${before.length})`} job={job} authFetch={authFetch} onUpdated={onUpdated} />
        <UploadBtn kind="after" label={`After (${after.length})`} job={job} authFetch={authFetch} onUpdated={onUpdated} />
        <button type="button" onClick={() => setSigning(true)} className="h-11 px-3 rounded-md border border-slate-200 bg-white text-sm font-bold text-[color:var(--mv-navy)] inline-flex items-center gap-1.5 hover:border-[color:var(--mv-orange)]" data-testid={`proof-signature-btn-${job.ref}`}>
          <PenLine className="w-4 h-4 text-[color:var(--mv-orange)]" /> {job.signature_url ? 'Re-sign' : 'Get signature'}
        </button>
      </div>
      {before.length > 0 && <div><div className="text-[10px] font-bold text-slate-500 mb-1">Before</div><PhotoRow photos={before} testid={`proof-before-photos-${job.ref}`} /></div>}
      {after.length > 0 && <div><div className="text-[10px] font-bold text-slate-500 mb-1">After</div><PhotoRow photos={after} testid={`proof-after-photos-${job.ref}`} /></div>}
      {job.signature_url && (
        <div>
          <div className="text-[10px] font-bold text-slate-500 mb-1">Signed ✓</div>
          <img src={resolveUrl(job.signature_url)} alt="Customer signature" className="h-16 rounded border border-slate-200 bg-white" data-testid={`proof-signature-img-${job.ref}`} />
        </div>
      )}
      {signing && <SignaturePad onCancel={() => setSigning(false)} onSave={saveSignature} saving={savingSig} />}
    </div>
  );
};
